import { Popover, Portal } from "@headlessui/react";
import { router, usePage } from "@inertiajs/react";
import { createColumnHelper } from "@tanstack/react-table";
import axios from "axios";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { BiCheckCircle, BiSearchAlt } from "react-icons/bi";
import { FiDownload, FiEdit3, FiFileText, FiMoreVertical } from "react-icons/fi";
import { MdWarningAmber } from "react-icons/md";

import EnergyExpenseModal from "@/components/Accounts/EnergyExpenseModal";
import ErrorBadge from "@/components/Accounts/ErrorBadge";
import MissingAccountsAlert from "@/components/Accounts/MissingAccountsAlert";
import AlertTooltip from "@/components/AlertTooltip";
import SimpleButton from "@/components/Buttons/SimpleButton";
import { DistributorExpenseData } from "@/components/Cards/BillsInfo/types";
import FaqCardComponent from "@/components/Cards/FaqComponent";
import Dropdown from "@/components/Dropdown";
import Layout from "@/components/Layout";
import ApproveExpenseModal from "@/components/Modal/ApproveExpenseModal";
import CalculationDetailsModal from "@/components/Modal/CalculationDetailsModal";
import DistributorBillsModal, {
  DistributorBillItem,
} from "@/components/Modal/DistributorBillsModal";
import ExpenseStatusModal from "@/components/Modal/ExpenseStatusModal";
import Table from "@/components/Table";
import Typography from "@/components/Typography";
import {
  ButtonContainer,
  FiltersContainer,
  ListSection,
  PageContainer,
} from "@/constants/listIndexStyle";
import { AlertContext } from "@/contexts/Alert";
import { completMonth } from "@/utils/dateFormater";
import { getYearOptions } from "@/utils/getYearOptions";
import { moneyFormatterWithoutCurrency } from "@/utils/moneyFormat";
import { formatNumberWithCommas } from "@/utils/numberFormater";

import { SearchForm } from "../power-plants/style";
const yearsOptions: string[] = getYearOptions();
const logStatusOptions: string[] = [
  "Em processamento",
  "Falhou",
  "Bem sucedido",
  "Nenhum conta criada",
  "Todos",
];
const statusOptions: string[] = ["Aprovação pendente", "Não pago", "Pago", "Todos"];
const statusTranslate = {
  "Aprovação pendente": "processing",
  Falhou: "failed",
  "Bem sucedido": "succeeded",
  "Nenhum conta criada": "empty",
  "Em processamento": "processing",
  Todos: "",
  "": "",
};

type AccountActionsProps = {
  expense: any;
  onApprove: (expense: any) => void;
  onEdit: (expense: any) => void;
  onViewDistributorBills: (expense: any) => void;
};

type AccountActionsPanelProps = {
  close: () => void;
  panelPosition: { top: number; left: number };
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  expense: any;
  isApproved: boolean;
  hasMultipleFacilities: boolean;
  billsWithPdf: any[];
  onApprove: (expense: any) => void;
  onEdit: (expense: any) => void;
  onViewDistributorBills: (expense: any) => void;
};

const AccountActionsPanel = ({
  close,
  panelPosition,
  buttonRef,
  expense,
  isApproved,
  hasMultipleFacilities,
  billsWithPdf,
  onApprove,
  onEdit,
  onViewDistributorBills,
}: AccountActionsPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        close();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [close, buttonRef]);

  return (
    <Portal>
      <div
        ref={panelRef}
        className="absolute z-[9999] w-52 rounded-lg bg-white p-1 text-left shadow-lg ring-1 ring-black/5"
        style={{ top: panelPosition.top, left: panelPosition.left }}
      >
        {!isApproved && (
          <>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                close();
                onApprove(expense);
              }}
            >
              <BiCheckCircle size={16} />
              Aprovar
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                close();
                onEdit(expense);
              }}
            >
              <FiEdit3 size={16} />
              Editar
            </button>
          </>
        )}

        {hasMultipleFacilities ? (
          billsWithPdf.length > 0 ? (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                close();
                onViewDistributorBills(expense);
              }}
            >
              <FiFileText size={16} />
              Baixar contas de luz
            </button>
          ) : (
            <div className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-400">
              <FiFileText size={16} />
              Conta de luz indisponível
            </div>
          )
        ) : billsWithPdf.length === 1 ? (
          <a
            href={billsWithPdf[0].pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={() => close()}
          >
            <FiDownload size={16} />
            Baixar conta de luz
          </a>
        ) : (
          <div className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-400">
            <FiFileText size={16} />
            Conta de luz indisponível
          </div>
        )}
      </div>
    </Portal>
  );
};

const AccountActions = ({
  expense,
  onApprove,
  onEdit,
  onViewDistributorBills,
}: AccountActionsProps) => {
  const [panelPosition, setPanelPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const billsWithPdf = useMemo(
    () => (expense.distributor_bills || []).filter((b: any) => b.has_pdf && b.pdf_url),
    [expense.distributor_bills],
  );
  const totalBills = expense.distributor_bills?.length || 0;
  const isApproved = expense.approved_status === "approved";
  const hasMultipleFacilities =
    totalBills > 1 || (expense.facilities_count && expense.facilities_count > 1);

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            ref={buttonRef}
            type="button"
            className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
            aria-label="Abrir ações da conta"
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              setPanelPosition({
                top: rect.bottom + window.scrollY + 4,
                left: Math.max(8, rect.right + window.scrollX - 208),
              });
            }}
          >
            <FiMoreVertical size={18} />
          </Popover.Button>
          {open && panelPosition && (
            <AccountActionsPanel
              close={close}
              panelPosition={panelPosition}
              buttonRef={buttonRef}
              expense={expense}
              isApproved={isApproved}
              hasMultipleFacilities={hasMultipleFacilities}
              billsWithPdf={billsWithPdf}
              onApprove={onApprove}
              onEdit={onEdit}
              onViewDistributorBills={onViewDistributorBills}
            />
          )}
        </>
      )}
    </Popover>
  );
};

const ExpectedCreationStatus = ({ consumer }: { consumer: any }) => {
  if (!consumer.expected_creation_date) return "-";

  const expectedDate = new Date(`${consumer.expected_creation_date}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdue = expectedDate < today;
  const formattedDate = expectedDate.toLocaleDateString("pt-BR");

  if (!overdue) {
    return (
      <div className="flex w-full flex-col items-center gap-1 text-center">
        <span>Previsão de criação</span>
        <div className="text-xs text-gray-500">{formattedDate}</div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-center gap-1 text-center">
      <AlertTooltip
        type="error"
        size={16}
        message="Tivemos problema com a criação da fatura. Por favor, crie na mão."
        className="text-red-600 underline decoration-dotted underline-offset-2"
      >
        <MdWarningAmber size={16} />
        <span>Previsão</span>
      </AlertTooltip>
      <div className="text-center text-xs text-red-500">{formattedDate}</div>
    </div>
  );
};

const AccountsPage = ({ consumers, conglomerates, fiobConfiguration }) => {
  const { showAlert } = useContext<any>(AlertContext);
  const { errors }: any = usePage().props;
  const [filteredConsumers, setFilteredConsumers] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const totalPages = 1;
  const [loadingConsumers, setLoadingConsumers] = useState(false);
  const [loadedLogs, setLoadedLogs] = useState<any[]>([]);
  const [logsPage, setLogsPage] = useState<number>(1);
  const [hasMoreLogs, setHasMoreLogs] = useState(true);
  const columnHelper = createColumnHelper();
  const today = useMemo(() => new Date(), []);
  const [open, setOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<{
    expense: any;
    consumerName: string;
  } | null>(null);
  const [accountModal, setAccountModal] = useState<"create" | null>(null);
  const [initialConsumerId, setInitialConsumerId] = useState<number | string | null>(null);
  const [missingAccountsExpanded, setMissingAccountsExpanded] = useState(false);
  const [missingAccountsDismissed, setMissingAccountsDismissed] = useState(false);
  const [missingAccountsVisible, setMissingAccountsVisible] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [calculationModalOpen, setCalculationModalOpen] = useState(false);
  const [distributorBillsModalOpen, setDistributorBillsModalOpen] = useState(false);
  const [selectedDistributorBillsData, setSelectedDistributorBillsData] = useState<{
    consumerName: string;
    bills: DistributorBillItem[];
  }>({ consumerName: "", bills: [] });
  const [selectedConsumer, setSelectedConsumer] = useState({});
  const [selectedExpenseId, setSelectedExpenseId] = useState(null);
  const [selectedCalculationData, setSelectedCalculationData] = useState<{
    subRows: DistributorExpenseData[];
    month: string;
    discountPercentage: number;
  } | null>(null);
  const [filters, setFilters] = useState({
    year: new URLSearchParams(window.location.search).get("year")
      ? String(new URLSearchParams(window.location.search).get("year"))
      : String(today.getFullYear()),
    month: new URLSearchParams(window.location.search).get("month")
      ? completMonth[Number(new URLSearchParams(window.location.search).get("month")) - 1]
      : completMonth[today.getMonth() - 1],
    search: "",
    approved_status: "",
    status: "",
    conglomerate_name: new URLSearchParams(window.location.search).get("conglomerate_name"),
  });

  const accountRows = useMemo(
    () =>
      consumers.flatMap((consumer: any) => {
        const { subRows = [], ...consumerData } = consumer;

        return subRows.length > 0
          ? subRows.map((expense: any) => ({ ...consumerData, ...expense, is_placeholder: false }))
          : [{ ...consumerData, id: `consumer-${consumer.consumer_id}`, is_placeholder: true }];
      }),
    [consumers],
  );
  const missingConsumers = useMemo(
    () =>
      accountRows.filter((consumer: any) => {
        if (!consumer.is_placeholder) return false;
        if (!consumer.expected_creation_date) return false;

        const expectedDate = new Date(`${consumer.expected_creation_date}T00:00:00`);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return expectedDate < today;
      }),
    [accountRows],
  );
  const missingAccountsKey = `${filters.year}-${filters.month}-${filters.conglomerate_name || ""}`;

  // Troca de filtro: alerta desliza para baixo antes de o novo dado chegar
  useEffect(() => {
    setMissingAccountsDismissed(false);
    setMissingAccountsExpanded(false);
    setMissingAccountsVisible(false);
  }, [missingAccountsKey]);

  // Novo dado com contas faltando: espera o slide de saída terminar e sobe de novo
  useEffect(() => {
    if (missingConsumers.length === 0) return;

    const timer = setTimeout(() => setMissingAccountsVisible(true), 300);

    return () => clearTimeout(timer);
  }, [missingConsumers, missingAccountsKey]);

  const [logFilters, setLogFilters] = useState({
    year: new URLSearchParams(window.location.search).get("year")
      ? String(new URLSearchParams(window.location.search).get("year"))
      : String(today.getFullYear()),
    month: new URLSearchParams(window.location.search).get("month")
      ? completMonth[Number(new URLSearchParams(window.location.search).get("month")) - 1]
      : completMonth[today.getMonth() - 1],
    status: "",
  });

  const changeFilters = ({
    year = filters.year,
    month = filters.month,
    search = filters.search,
    approved_status = filters.approved_status,
    status = filters.status,
    conglomerate_name = filters.conglomerate_name,
  }) => {
    setPage(1);
    setFilters({ year, month, search, approved_status, status, conglomerate_name });
    setLogFilters({ ...logFilters, year: year, month: month });
  };

  const changeLogFilters = ({
    year = logFilters.year,
    month = logFilters.month,
    status = logFilters.status,
  }) => {
    setPage(1);
    setLogFilters({ year, month, status });
    setFilters({ ...filters, year: year, month: month });
  };

  const handleRowClick = (expenseId: number) => {
    const foundExpense = filteredConsumers.find((expense: any) => expense.id === expenseId);

    if (!foundExpense || foundExpense.is_placeholder) {
      return;
    }

    // Verificar se calculation_details existe e não está vazio
    if (!foundExpense.calculation_details || foundExpense.calculation_details.length === 0) {
      showAlert({
        message: "Os detalhes de cálculo não estão disponíveis para esta fatura.",
        type: "error",
      });
      return;
    }

    // Verifica se o preço da fatura foi modificado manualmente comparando com o cálculo detalhado
    // Soma os valores de GD1 e GD2 dos calculation_details e compara com o preço final
    // Usa tolerância de 1 centavo para ignorar erros de arredondamento de conversão float/int
    const actualPrice = Math.round(foundExpense.price * 100);
    const expectedPrice = Math.round(
      (foundExpense.calculation_details as any[]).reduce(
        (sum: number, detail: any) => sum + (detail.price || 0),
        0,
      ) * 100,
    );
    const tolerance = 1;
    const isManuallyModified = Math.abs(actualPrice - expectedPrice) > tolerance;

    if (isManuallyModified) {
      showAlert({
        message:
          "Esta fatura foi modificada manualmente. Os detalhes de cálculo podem não refletir o valor final.",
        type: "warning",
      });
    }

    // Montar os dados para a modal
    setSelectedCalculationData({
      subRows: foundExpense.calculation_details,
      month: filters.month,
      discountPercentage: foundExpense.discount_percentage,
    });

    setCalculationModalOpen(true);
  };

  const showMore = () => page !== totalPages && filteredConsumers.length !== 0;

  const openCreateAccount = (consumerId: number | string) => {
    setInitialConsumerId(consumerId);
    setAccountModal("create");
  };

  const columns = [
    columnHelper.accessor("consumer_name", {
      header: () => "Cliente/SPE",
      cell: (info: any) => {
        return (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{info.row.original.consumer_name}</span>
            <ErrorBadge row={info.row.original} />
          </div>
        );
      },
    }),
    columnHelper.accessor("facilities_count", {
      header: () => "Quantidade UC",
      cell: (info: any) => {
        return (
          <div className="flex w-full justify-center">
            <span className="rounded-md bg-gray-50 px-2 py-1">
              {info.row.original.facilities_count || "-"}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor("distributor_expenses_count", {
      header: () => "Quantidade CD",
      cell: (info: any) => info.row.original.distributor_expenses_count || "-",
    }),
    columnHelper.accessor("price", {
      header: () => "Valor",
      cell: (info: any) => {
        return !info.row.original.is_placeholder && info.row.original.price !== "-"
          ? `R$${moneyFormatterWithoutCurrency(info.row.original.price)}`
          : "-";
      },
    }),
    columnHelper.accessor("discount", {
      header: () => "Desconto",
      cell: (info: any) => {
        return !info.row.original.is_placeholder && info.row.original.discount_percentage !== "-"
          ? `${(info.row.original.discount_percentage * 100).toFixed(2)}%`
          : "-";
      },
    }),
    columnHelper.accessor("kwh", {
      header: () => "Consumo",
      cell: (info: any) => {
        return !info.row.original.is_placeholder && info.row.original.kwh !== "-"
          ? `${formatNumberWithCommas(info.row.original.kwh)} kwh`
          : "-";
      },
    }),
    columnHelper.accessor("compensated_kwh", {
      header: () => "Injetado",
      cell: (info: any) => {
        return !info.row.original.is_placeholder && info.row.original.compensated_kwh !== "-"
          ? `${formatNumberWithCommas(info.row.original.compensated_kwh ?? 0)} kwh`
          : "-";
      },
    }),
    columnHelper.accessor("status", {
      header: () => "Status",
      cell: (info: any) => {
        if (info.row.original.is_placeholder) {
          return <ExpectedCreationStatus consumer={info.row.original} />;
        }

        let statusText = "-";
        if (
          info.row.original.approved_status == null ||
          info.row.original.approved_status === "pending"
        ) {
          statusText = "Aprovação pendente";
        } else if (info.row.original.payment_status === "paid") {
          statusText = "pago";
        } else {
          statusText = "não pago";
        }

        return (
          <div className="flex flex-col gap-1">
            <span>{statusText}</span>
            {info.row.original.has_payment_error && (
              <div className="flex items-center gap-1 text-xs text-red-500">
                <MdWarningAmber size={14} />
                <span>Erro na API de pagamento</span>
              </div>
            )}
            {info.row.original.has_fallback_warnings && (
              <div className="flex items-center gap-1 text-xs text-amber-500">
                <MdWarningAmber size={14} />
                <span>{info.row.original.fallback_warnings?.join(", ")}</span>
              </div>
            )}
            {info.row.original.due_date && (
              <div className="text-xs text-gray-500">
                Vencimento: {new Date(info.row.original.due_date).toLocaleDateString("pt-BR")}
              </div>
            )}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "account_actions",
      header: () => "Ações",
      cell: (info: any) => {
        if (info.row.original.is_placeholder) return "-";

        return (
          <div className="flex w-full justify-center">
            <AccountActions
              expense={info.row.original}
              onApprove={(expense) => {
                setSelectedConsumer(expense);
                setSelectedExpenseId(expense.id);
                setApproveOpen(true);
              }}
              onEdit={(expense) => {
                setEditingAccount({
                  expense,
                  consumerName: expense.consumer_name || "",
                });
              }}
              onViewDistributorBills={(expense) => {
                setSelectedDistributorBillsData({
                  consumerName: expense.consumer_name || expense.name || "",
                  bills: expense.distributor_bills || [],
                });
                setDistributorBillsModalOpen(true);
              }}
            />
          </div>
        );
      },
    }),
  ];

  const fetchLogs = useCallback(
    (
      logYear: string,
      validMonthIndex: number,
      validStatus: string,
      logPage: number,
      refresh: boolean,
    ) => {
      axios
        .get(
          `/manager/accounts.json?logYear=${logYear}&logMonth=${validMonthIndex}&logStatus=${validStatus}&logPage=${logPage}`,
        )
        .then((response) => {
          if (refresh) {
            setLoadedLogs(response.data.logs);
          } else {
            setLoadedLogs((loadedLogs) => [...loadedLogs, ...response.data.logs]);
          }
          setHasMoreLogs(response.data.has_more);
        });
    },
    [],
  );

  useEffect(() => {
    let filtered = accountRows.filter((consumer: any) =>
      consumer.consumer_name.toLowerCase().includes(filters.search.toLowerCase()),
    );
    if (filters.status) {
      filtered = filtered.filter((consumer: any) => consumer.payment_status == filters.status);
    }
    if (filters.approved_status) {
      filtered = filtered.filter(
        (consumer: any) => consumer.approved_status == filters.approved_status,
      );
    }

    setFilteredConsumers(filtered);
  }, [filters.search, filters.status, filters.approved_status, accountRows]);

  useEffect(() => {
    if (errors?.energy_expense) {
      showAlert({ message: errors.energy_expense, type: "error" });
    }
  }, [errors?.energy_expense, showAlert]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilters = {
      year: urlParams.get("year") || String(today.getFullYear()),
      month: urlParams.get("month") || String(today.getMonth() + 1),
      search: urlParams.get("search") || "",
      approved_status: urlParams.get("approved_status") || "",
      status: urlParams.get("status") || "",
      conglomerate_name: urlParams.get("conglomerate_name") || "",
    };
    const monthIndex = completMonth.indexOf(filters.month) + 1;
    const currentFiltersMatchUrl =
      page === 1 &&
      filters.year === urlFilters.year &&
      String(monthIndex) === urlFilters.month &&
      filters.search === urlFilters.search &&
      filters.approved_status === urlFilters.approved_status &&
      filters.status === urlFilters.status &&
      (filters.conglomerate_name || "") === urlFilters.conglomerate_name;

    if (currentFiltersMatchUrl) {
      setLoadingConsumers(false);
      return;
    }

    const validMonthIndex = monthIndex > 0 ? monthIndex : 1; // Use 1 (janeiro) se o índice for inválido

    const options = {
      page: page,
      name: filters.search,
    };
    setLoadingConsumers(true);

    router.reload({
      data: {
        year: filters.year,
        month: validMonthIndex,
        search: filters.search,
        approved_status: filters.approved_status,
        status: filters.status,
        conglomerate_name: filters.conglomerate_name,
        options,
      },
      onFinish: () => {
        setLoadingConsumers(false);
      },
    });
  }, [filters, page, today]);

  // Ao abrir a modal ou trocar filtros, reseta a página para 1.
  // O fetch é feito pelo useEffect abaixo, reagindo a logsPage/logFilters/open.
  useEffect(() => {
    if (!open) {
      return;
    }

    setLogsPage(1);
  }, [logFilters, open]);

  const changeLogsPage = (page: number) => {
    setLogsPage(page);
  };

  useEffect(() => {
    if (open) {
      const monthIndex = completMonth.indexOf(logFilters.month) + 1;
      const validMonthIndex = monthIndex > 0 ? monthIndex : 1;

      fetchLogs(
        logFilters.year,
        validMonthIndex,
        statusTranslate[logFilters.status],
        logsPage,
        logsPage == 1,
      );
    }
  }, [fetchLogs, logFilters.month, logFilters.status, logFilters.year, logsPage, open]);

  const exportTable = () => {
    const data: string[][] = [
      [
        "Cliente",
        "Usina",
        "Quantidade UC",
        "Quantidade CD",
        "Valor",
        "Desconto",
        "Consumo",
        "Injetado",
      ],
    ];

    filteredConsumers.forEach((row: any) => {
      data.push([
        row.consumer_name,
        row.consortium_name ?? "-",
        row.facilities_count ?? "-",
        row.distributor_expenses_count ?? "-",
        row.price ?? "-",
        row.discount_percentage ?? "-",
        row.kwh ?? "-",
        row.compensated_kwh ?? "-",
      ]);
    });

    const csv = "data:text/csv;charset=utf-8," + data.map((e) => e.join(";")).join("\n");
    const encodedUri = encodeURI(csv);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "geedee-consumo.csv");
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  };

  const conglomeratesOptions = useMemo(
    () => ["Todos", ...conglomerates.map((conglomerate: any) => conglomerate.name)],
    [conglomerates],
  );

  return (
    <Layout>
      {editingAccount && (
        <EnergyExpenseModal
          mode="edit"
          consumers={accountRows}
          consumerOptions={consumers}
          filters={filters}
          expense={editingAccount.expense}
          consumerName={editingAccount.consumerName}
          onClose={() => setEditingAccount(null)}
          onSaved={(message) => {
            setEditingAccount(null);
            showAlert({ message, type: "success" });
          }}
        />
      )}
      {accountModal === "create" && (
        <EnergyExpenseModal
          mode="create"
          consumers={accountRows}
          consumerOptions={consumers}
          initialConsumerId={initialConsumerId}
          filters={filters}
          onClose={() => {
            setAccountModal(null);
            setInitialConsumerId(null);
          }}
          onSaved={(message) => {
            setAccountModal(null);
            setInitialConsumerId(null);
            showAlert({ message, type: "success" });
          }}
          onEditExisting={(expense, name) => {
            setEditingAccount({ expense, consumerName: name });
          }}
        />
      )}
      <ExpenseStatusModal
        isOpen={open}
        setIsOpen={setOpen}
        info={loadedLogs}
        logsPage={logsPage}
        changePage={changeLogsPage}
        yearsOptions={yearsOptions}
        hasMoreLogs={hasMoreLogs}
        changeFilters={changeLogFilters}
        filters={logFilters}
        completMonth={completMonth}
        statusOptions={logStatusOptions}
      />
      <ApproveExpenseModal
        isOpen={approveOpen}
        setIsOpen={setApproveOpen}
        referenceMonth={filters}
        consumer={selectedConsumer}
        expenseId={selectedExpenseId}
      />
      {selectedCalculationData && (
        <CalculationDetailsModal
          isOpen={calculationModalOpen}
          setIsOpen={setCalculationModalOpen}
          subRows={selectedCalculationData.subRows}
          month={selectedCalculationData.month}
          discountPercentage={selectedCalculationData.discountPercentage}
        />
      )}
      <DistributorBillsModal
        isOpen={distributorBillsModalOpen}
        setIsOpen={setDistributorBillsModalOpen}
        consumerName={selectedDistributorBillsData.consumerName}
        referenceMonth={{ month: filters.month, year: filters.year }}
        bills={selectedDistributorBillsData.bills}
      />
      <PageContainer>
        <div className="mb-6 flex items-center justify-between gap-4">
          <Typography variant="h3">Contas dos Clientes</Typography>
          <SimpleButton
            variant="primary"
            label="Criar conta Geedee"
            onClick={() => {
              setInitialConsumerId(null);
              setAccountModal("create");
            }}
            className="!w-fit px-5"
          />
        </div>
        <ListSection className="flex-col md:flex-row">
          <Typography variant="h4">Listagem</Typography>
          <FiltersContainer className="pt-5 md:pt-0">
            {loadingConsumers && (
              <div className="my-auto size-6 animate-spin rounded-full border-4 border-solid border-black border-t-transparent pt-3" />
            )}
            <SearchForm className="search" onSubmit={(e) => e.preventDefault()}>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Buscar por cliente"
              />
              <button className="search-icon">
                <BiSearchAlt />
              </button>
            </SearchForm>
            <Dropdown
              options={yearsOptions}
              onSelect={(value) => {
                changeFilters({ year: value });
              }}
              defaultValue={filters.year}
            />
            <Dropdown
              options={completMonth}
              onSelect={(value) => {
                changeFilters({ month: value });
              }}
              defaultValue={filters.month}
            />
            <Dropdown
              options={conglomeratesOptions}
              onSelect={(value) => {
                changeFilters({ conglomerate_name: value });
              }}
              defaultValue={filters.conglomerate_name ?? undefined}
            />
            <Dropdown
              options={statusOptions}
              onSelect={(value) => {
                if (value == "Aprovação pendente") {
                  changeFilters({ approved_status: "pending", status: "" });
                } else if (value == "não pago") {
                  changeFilters({ approved_status: "", status: "pending" });
                } else if (value == "pago") {
                  changeFilters({ approved_status: "", status: "paid" });
                } else {
                  changeFilters({ approved_status: "", status: "" });
                }
              }}
              defaultValue={"todos"}
            />
          </FiltersContainer>
        </ListSection>
        <Table
          data={filteredConsumers}
          columns={columns}
          onClickRow={handleRowClick}
          disableRowHover
        />
        <div className="flex w-[500px] flex-row gap-4">
          <button
            className="flex h-[40px] w-full cursor-pointer flex-row items-center justify-center rounded-full border-[none] bg-slate-950 p-4 text-white"
            onClick={() => {
              setOpen(true);
            }}
          >
            Histórico de criação
          </button>
          <button
            className="flex h-[40px] w-full cursor-pointer flex-row items-center justify-center rounded-full border-[none] bg-slate-950 p-4 text-white"
            onClick={exportTable}
          >
            Exportar tabela
          </button>
        </div>
        {showMore() && (
          <ButtonContainer>
            <SimpleButton
              variant="simple"
              label=" + Carregar mais"
              onClick={() => setPage(page + 1)}
            />
          </ButtonContainer>
        )}

        {fiobConfiguration && (
          <div className="mb-6 mt-20 rounded-lg bg-white p-4">
            <Typography variant="h5">Informações FIOB</Typography>
            <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
              <div>
                <span className="text-sm text-gray-600">Preço KWh: </span>
                <span className="font-semibold">
                  R$ {fiobConfiguration.kwh_price.toFixed(4)}/Kwh
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Porcentagem: </span>
                <span className="font-semibold">{fiobConfiguration.percentage}%</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Custo Líquido: </span>
                <span className="font-semibold">
                  R$ {fiobConfiguration.fiob_cost.toFixed(5)}/Kwh
                </span>
              </div>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              Vigente desde:{" "}
              {new Date(fiobConfiguration.effective_date).toLocaleDateString("pt-BR")}
            </div>
          </div>
        )}
        <div className="pt-18">
          <FaqCardComponent variant="questionSection" />
        </div>
      </PageContainer>
      <MissingAccountsAlert
        consumers={missingConsumers}
        month={filters.month}
        year={filters.year}
        dismissed={missingAccountsDismissed}
        visible={missingAccountsVisible}
        expanded={missingAccountsExpanded}
        onCreateAccount={openCreateAccount}
        onDismiss={() => setMissingAccountsDismissed(true)}
        onExpand={() => setMissingAccountsExpanded(true)}
        onCollapse={() => setMissingAccountsExpanded(false)}
      />
    </Layout>
  );
};

export default AccountsPage;
